import { useRef, useEffect, useMemo, useState } from 'react';
import { TIME_GUTTER_CLASS } from '../constants';
import { HOUR_HEIGHT_PX } from '../types';
import type { EventInstance } from '../types';
import type { TimeFormat } from '../types';
import {
  positionTimedEvents,
  isToday,
  formatDateKey,
  formatTime,
  findOverlappingEventIds,
  findInsufficientBreaks,
  isMultiDayBarEvent,
  startOfDay,
  endOfDay,
} from '../utils';
import { TimeGridEvent } from './TimeGridEvent';

interface TimeGridProps {
  days: Date[];
  events: EventInstance[];
  workingHoursStart: number;
  timeFormat?: TimeFormat;
  /** A click or a drag picked out this range; open the create flow for it. */
  onSelectRange: (start: Date, end: Date) => void;
  onClickEvent: (event: EventInstance) => void;
  resizingEventId?: string | null;
  resizeHeight?: number | null;
  onResizeStart?: (eventId: string, originalHeight: number, e: React.MouseEvent) => void;
  draggingEventId?: string | null;
  dragOffset?: { x: number; y: number } | null;
  onEventDragStart?: (eventId: string, e: React.MouseEvent) => void;
  hourHeightPx?: number;
  dimPastEvents?: boolean;
  eventColorPriority?: 'event' | 'calendar';
  showConflictWarnings?: boolean;
  minBreakBetweenEvents?: number;
  snapMinutes?: number;
  defaultDurationMinutes?: number;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES_PER_DAY = 24 * 60;

// Touch only: a press this long without moving starts a selection drag. A
// shorter press is a tap (create at that time) and any movement before the
// timer fires hands the gesture back to the browser as a scroll.
const LONG_PRESS_MS = 400;
const MOVE_CANCEL_THRESHOLD_PX = 5;

interface DragSelection {
  dayKey: string;
  /** Minutes from midnight where the gesture started — may exceed `endMin`. */
  anchorMin: number;
  currentMin: number;
}

/** Minutes from midnight at `clientY`, snapped and clamped to the day. */
function minutesAtPointer(
  clientY: number,
  columnEl: HTMLElement,
  hourHeight: number,
  snapMinutes: number,
  rounding: 'floor' | 'nearest',
): number {
  const raw = ((clientY - columnEl.getBoundingClientRect().top) / hourHeight) * 60;
  const snap = Math.max(1, snapMinutes);
  const snapped =
    rounding === 'floor' ? Math.floor(raw / snap) * snap : Math.round(raw / snap) * snap;
  return Math.min(MINUTES_PER_DAY, Math.max(0, snapped));
}

function atMinutes(day: Date, minutes: number): Date {
  const d = new Date(day);
  d.setHours(0, 0, 0, 0);
  d.setMinutes(minutes);
  return d;
}

export function TimeGrid({
  days,
  events,
  workingHoursStart,
  timeFormat = '12h',
  onSelectRange,
  onClickEvent,
  resizingEventId,
  resizeHeight,
  onResizeStart,
  draggingEventId,
  dragOffset,
  onEventDragStart,
  hourHeightPx: hourHeight = HOUR_HEIGHT_PX,
  dimPastEvents = false,
  eventColorPriority = 'event',
  showConflictWarnings = false,
  minBreakBetweenEvents = 0,
  snapMinutes = 15,
  defaultDurationMinutes = 60,
}: TimeGridProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [selection, setSelection] = useState<DragSelection | null>(null);

  // Compute which events have conflicts across all visible days
  const conflictingIds = useMemo(() => {
    if (!showConflictWarnings) return new Set<string>();
    const allIds = new Set<string>();
    for (const day of days) {
      const dayStart = startOfDay(day);
      const dayEnd = endOfDay(day);
      const dayEvents = events.filter((e) => {
        // Same set the grid actually draws — multi-day bars live in the all-day
        // band, and flagging every meeting inside a three-day conference as a
        // conflict is noise, not a warning.
        if (isMultiDayBarEvent(e)) return false;
        const s = new Date(e.startAt);
        const end = new Date(e.endAt);
        return s < dayEnd && end > dayStart;
      });
      const ids = findOverlappingEventIds(dayEvents);
      ids.forEach((id) => allIds.add(id));
    }
    return allIds;
  }, [showConflictWarnings, days, events]);

  // Compute insufficient break gaps per day
  const breakWarningsByDay = useMemo(() => {
    if (minBreakBetweenEvents <= 0)
      return new Map<string, ReturnType<typeof findInsufficientBreaks>>();
    const map = new Map<string, ReturnType<typeof findInsufficientBreaks>>();
    for (const day of days) {
      const warnings = findInsufficientBreaks(events, day, minBreakBetweenEvents);
      if (warnings.length > 0) {
        map.set(formatDateKey(day), warnings);
      }
    }
    return map;
  }, [minBreakBetweenEvents, days, events]);

  // Now-line: re-render every minute so the indicator slides down through
  // the day. Only the today column renders it (see `isToday(day)` below).
  const [now, setNow] = useState(() => new Date());
  const showsToday = useMemo(() => days.some((d) => isToday(d)), [days]);
  useEffect(() => {
    if (!showsToday) return;
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, [showsToday]);
  const nowTopPx = (now.getHours() + now.getMinutes() / 60) * hourHeight;

  /**
   * Slot interaction, Google-Calendar style.
   *
   * Mouse: press starts a selection at that minute, dragging sets its length,
   * release opens the create flow for the range. A press with no movement is a
   * click and gets the user's default duration.
   *
   * Touch: a tap creates at that time and a plain drag is left alone so the
   * browser scrolls natively. Only a deliberate ~400ms hold starts a selection
   * — while one is active we preventDefault touchmove (a non-passive listener,
   * which React's own handlers cannot be) so the page does not scroll out from
   * under the drag.
   */
  const handleSlotPointerDown = (day: Date) => (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    const columnEl = e.currentTarget.parentElement;
    if (!columnEl) return;

    const dayKey = formatDateKey(day);
    const isMouse = e.pointerType === 'mouse';
    const startX = e.clientX;
    const startY = e.clientY;
    const anchorMin = minutesAtPointer(e.clientY, columnEl, hourHeight, snapMinutes, 'floor');

    let phase: 'pending' | 'dragging' | 'done' = isMouse ? 'dragging' : 'pending';
    let moved = false;
    let currentMin = anchorMin;

    if (phase === 'dragging') setSelection({ dayKey, anchorMin, currentMin });

    // Non-passive so a touch selection can suppress the page scroll.
    const blockTouchScroll = (te: TouchEvent) => {
      if (phase === 'dragging') te.preventDefault();
    };

    const timer = window.setTimeout(() => {
      if (phase !== 'pending') return;
      phase = 'dragging';
      setSelection({ dayKey, anchorMin, currentMin });
    }, LONG_PRESS_MS);

    const onMove = (mv: PointerEvent) => {
      if (phase === 'done') return;

      if (phase === 'pending') {
        // Before the hold completes, any real movement is a scroll — let go.
        if (
          Math.abs(mv.clientX - startX) < MOVE_CANCEL_THRESHOLD_PX &&
          Math.abs(mv.clientY - startY) < MOVE_CANCEL_THRESHOLD_PX
        ) {
          return;
        }
        window.clearTimeout(timer);
        phase = 'done';
        cleanup();
        return;
      }

      if (Math.abs(mv.clientY - startY) >= MOVE_CANCEL_THRESHOLD_PX) moved = true;
      currentMin = minutesAtPointer(mv.clientY, columnEl, hourHeight, snapMinutes, 'nearest');
      setSelection({ dayKey, anchorMin, currentMin });
    };

    const onUp = () => {
      const endedIn = phase;
      cleanup();
      // 'done' means the gesture was already handed off (a touch scroll).
      if (endedIn === 'done') return;

      // Still 'pending' on release = a touch tap that never became a hold.
      // A drag that never left its starting slot counts as a click too.
      const startMin = Math.min(anchorMin, currentMin);
      const endMin = Math.max(anchorMin, currentMin);
      const isClick =
        endedIn === 'pending' || !moved || endMin - startMin < Math.max(1, snapMinutes);

      const from = atMinutes(day, isClick ? anchorMin : startMin);
      const to = isClick
        ? new Date(from.getTime() + defaultDurationMinutes * 60000)
        : atMinutes(day, endMin);
      onSelectRange(from, to);
    };

    const onCancel = () => cleanup();

    const cleanup = () => {
      window.clearTimeout(timer);
      phase = 'done';
      setSelection(null);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      window.removeEventListener('touchmove', blockTouchScroll);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    window.addEventListener('touchmove', blockTouchScroll, { passive: false });
  };

  // Auto-scroll on mount and when the visible range changes (switching
  // Day↔Week, navigating dates). If today is in view, anchor the current
  // time about a third of the way down the viewport so the upcoming
  // hours are visible below the now-line. Otherwise fall back to
  // working hours. `daysKey` is a stable signal — `days` is a new array
  // reference on every render.
  const daysKey = useMemo(() => days.map((d) => formatDateKey(d)).join('|'), [days]);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (showsToday) {
      const n = new Date();
      const nowHour = n.getHours() + n.getMinutes() / 60;
      const targetTop = nowHour * hourHeight - el.clientHeight / 3;
      el.scrollTop = Math.max(0, targetTop);
    } else {
      el.scrollTop = Math.max(0, workingHoursStart * hourHeight - 20);
    }
  }, [workingHoursStart, hourHeight, showsToday, daysKey]);

  return (
    <div
      ref={scrollRef}
      className="flex-1 overflow-y-scroll overflow-x-hidden"
      // Always scrolling (not `auto`) so the gutter is reserved whether or not
      // the content overflows — otherwise the day columns change width the
      // moment a scrollbar appears and drift out of line with the headers,
      // which reserve the same width unconditionally.
      style={{ scrollbarGutter: 'stable' }}
    >
      <div className="flex" style={{ minHeight: 24 * hourHeight }}>
        {/* Hour labels */}
        <div className={TIME_GUTTER_CLASS}>
          {HOURS.map((hour) => (
            <div
              key={hour}
              className="text-[10px] text-muted-foreground text-right pr-1 sm:pr-2 relative"
              style={{ height: hourHeight }}
            >
              <span className="absolute -top-[7px] right-1 sm:right-2 whitespace-nowrap">
                {hour === 0 ? '' : formatTime(new Date(2000, 0, 1, hour), timeFormat)}
              </span>
            </div>
          ))}
        </div>

        {/* Day columns */}
        <div
          className="flex-1 grid relative min-w-0"
          style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}
        >
          {days.map((day) => {
            const dayKey = formatDateKey(day);
            const positioned = positionTimedEvents(events, day, hourHeight);
            const todayClass = isToday(day) ? 'bg-primary/5' : '';

            return (
              <div
                key={dayKey}
                // Read by the drag hook's drop hit-test to resolve the target
                // day — the same marker month cells carry. Removing it
                // silently reduces dragging to time-only.
                data-calendar-date={dayKey}
                className={`border-r last:border-r-0 relative min-w-0 ${todayClass}`}
              >
                {/* Hour grid lines */}
                {HOURS.map((hour) => (
                  <div
                    key={hour}
                    role="button"
                    tabIndex={0}
                    aria-label={formatTime(new Date(2000, 0, 1, hour), timeFormat)}
                    className="border-b border-dashed border-muted hover:bg-muted/30 select-none touch-pan-y"
                    style={{ height: hourHeight }}
                    onPointerDown={handleSlotPointerDown(day)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        const start = atMinutes(day, hour * 60);
                        onSelectRange(
                          start,
                          new Date(start.getTime() + defaultDurationMinutes * 60000),
                        );
                      }
                    }}
                  />
                ))}

                {/* In-progress drag selection */}
                {selection?.dayKey === dayKey &&
                  (() => {
                    const from = Math.min(selection.anchorMin, selection.currentMin);
                    const to = Math.max(selection.anchorMin, selection.currentMin);
                    const height = Math.max(((to - from) / 60) * hourHeight, 4);
                    return (
                      <div
                        className="absolute left-0 right-0 z-10 rounded-sm border border-primary/70 bg-primary/25 px-1 pointer-events-none overflow-hidden"
                        style={{ top: (from / 60) * hourHeight, height }}
                        aria-hidden="true"
                      >
                        <span className="text-[10px] font-medium text-primary truncate block">
                          {formatTime(atMinutes(day, from), timeFormat)} –{' '}
                          {formatTime(atMinutes(day, to), timeFormat)}
                        </span>
                      </div>
                    );
                  })()}

                {/* Positioned events */}
                {positioned.map((pos) => (
                  <TimeGridEvent
                    key={pos.event.id}
                    positioned={pos}
                    onClick={() => onClickEvent(pos.event)}
                    timeFormat={timeFormat}
                    resizingHeight={resizingEventId === pos.event.id ? resizeHeight : undefined}
                    onResizeStart={onResizeStart}
                    onDragStart={onEventDragStart}
                    dragOffset={draggingEventId === pos.event.id ? dragOffset : null}
                    dimPastEvents={dimPastEvents}
                    eventColorPriority={eventColorPriority}
                    hasConflict={conflictingIds.has(pos.event.eventId)}
                  />
                ))}

                {/* Now-line — only on today's column */}
                {isToday(day) && (
                  <div
                    className="absolute left-0 right-0 z-20 pointer-events-none"
                    style={{ top: nowTopPx - 1, height: 2 }}
                    aria-hidden="true"
                  >
                    <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-red-500" />
                    <div className="h-0.5 w-full bg-red-500" />
                  </div>
                )}

                {/* Insufficient break indicators */}
                {breakWarningsByDay.get(dayKey)?.map((w, i) => {
                  const endBefore = new Date(w.eventBefore.endAt);
                  const startAfter = new Date(w.eventAfter.startAt);
                  const midMinutes =
                    (endBefore.getHours() * 60 +
                      endBefore.getMinutes() +
                      startAfter.getHours() * 60 +
                      startAfter.getMinutes()) /
                    2;
                  const topPx = (midMinutes / 60) * hourHeight;

                  return (
                    <div
                      key={`break-${i}`}
                      className="absolute left-1 right-1 flex items-center justify-center pointer-events-none z-10"
                      style={{ top: topPx - 6, height: 12 }}
                      title={`${w.gapMinutes}min break`}
                    >
                      <div className="border-t border-dashed border-amber-400 dark:border-amber-500 w-full" />
                      <span className="absolute bg-amber-100 dark:bg-amber-900/60 text-amber-700 dark:text-amber-300 text-[9px] px-1 rounded">
                        {w.gapMinutes}m
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
